import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Form, ProgressBar, Modal } from 'react-bootstrap';
import axios from 'axios';
import { getInitialLoginData } from '../../../redux/slices/userSlice';
import '../order-tracker.scss';
import StepTracker from './StepTracker';

const WaitingOnAssets = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { initialLoginData } = useSelector((state) => state.user);
    const { user } = useSelector((state) => state.user);
    const account = initialLoginData?.Account;
    const agreements = initialLoginData?.Agreements;
    const { agreementID } = useParams();
    const advertiserCode = account?.AdvertiserCode;
    const fileInputRef = useRef();
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [fileProgress, setFileProgress] = useState({});
    const [formData, setFormData] = useState({
        productDescription: '',
        slogan: '',
        contactInfo: '',
        businessName: '',
        socialMedia: '',
        certificationNumbers: '',
        specialOffers: '',
        productionNotes: ''
    });

    const logDiagnostic = async (stage, extraDetails = {}) => {
        try {
            await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/CRM/LogFrontendDiagnostic`,
                {
                    userEmail: user?.Email || user?.email || 'Unknown',
                    stage: stage,
                    details: {
                        // Core validation info
                        hasToken: !!localStorage.getItem('token'),
                        tokenLength: localStorage.getItem('token')?.length || 0,
                        hasUser: !!user,
                        userID: user?.UserID || null,
                        userIDType: typeof user?.UserID,

                        // Agreement ID - the likely culprit
                        agreementIDFromParams: agreementID,
                        agreementIDFromParamsType: typeof agreementID,
                        agreementIDRefCurrent: agreementIDRef.current,
                        agreementIDRefType: typeof agreementIDRef.current,

                        // Context
                        advertiserCode: advertiserCode,
                        url: window.location.href,
                        pathname: window.location.pathname,
                        search: window.location.search,

                        // Form data
                        hasContactInfo: !!formData.contactInfo,
                        contactInfoLength: formData.contactInfo?.length || 0,

                        // Extra details
                        timestamp: new Date().toISOString(),
                        ...extraDetails
                    }
                },
                { timeout: 2000 }
            );
        } catch (err) {
            console.error('Failed to log diagnostic:', err);
        }
    };

    // ===== RACE CONDITION FIX: Capture agreementID at mount =====
    const agreementIDRef = useRef(null);

    useEffect(() => {
        // Capture agreementID when component mounts or when it changes
        if (agreementID) {
            const parsed = parseInt(agreementID, 10);
            if (!isNaN(parsed) && parsed > 0) {
                agreementIDRef.current = parsed;
                console.log('Captured agreementID on mount:', parsed);
            } else {
                console.error('Invalid agreementID on mount:', agreementID);
            }
        }
    }, [agreementID]);

    const handleFileUpload = async (file) => {
        console.log(file)
        const formData = new FormData();
        formData.append('file', file);
        formData.append('advertiserCode', advertiserCode);

        try {
            await axios.post(`${process.env.REACT_APP_BASE_URL}/api/AdPortalUpload/UploadFile`, formData, {
                onUploadProgress: progressEvent => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setFileProgress(prevProgress => ({ ...prevProgress, [file.name]: progress }));
                }
            });
        } catch (error) {
            console.error('Error uploading file:', error);
        }
    };

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        await Promise.all(files.map(file => handleFileUpload(file)));
    };


    const handleInputChange = (event) => {
        setFormData({ ...formData, [event.target.name]: event.target.value });
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const token = localStorage.getItem('token');

        // ===== LOG: Submission attempt =====
        await logDiagnostic('SUBMIT_CLICKED');

        // Check if token exists
        if (!token) {
            alert('Your session has expired. Please log in again.');
            navigate('/login');
            return;
        }
        if (isSubmitting) return;
        setIsSubmitting(true);

        // ===== CRITICAL: Capture values BEFORE any async operations =====
        const capturedAgreementID = agreementIDRef.current || parseInt(agreementID, 10);

        // ===== LOG: After capturing agreementID =====
        await logDiagnostic('AGREEMENTID_CAPTURED', {
            agreementIDFromParams: agreementID,
            agreementIDRefCurrent: agreementIDRef.current,
            capturedAgreementID: capturedAgreementID,
            capturedIsNaN: isNaN(capturedAgreementID),
            capturedLessThanOrEqualZero: capturedAgreementID <= 0
        });

        console.log('=== FORM SUBMISSION START ===');
        console.log('agreementID from useParams:', agreementID);
        console.log('agreementIDRef.current:', agreementIDRef.current);
        console.log('capturedAgreementID:', capturedAgreementID);
        console.log('user.UserID:', user?.UserID);
        console.log('advertiserCode:', advertiserCode);
        console.log('============================');

        // Validate all required fields
        if (!user?.UserID) {
            await logDiagnostic('VALIDATION_FAILED_NO_USERID', {
                hasUser: !!user,
                userKeys: user ? Object.keys(user) : [],
                userObject: user
            });
            console.error('Missing UserID', { user });
            alert('Session error: User information missing. Please log in again.');
            setIsSubmitting(false);
            return;
        }

        if (!capturedAgreementID || isNaN(capturedAgreementID) || capturedAgreementID <= 0) {
            await logDiagnostic('VALIDATION_FAILED_INVALID_AGREEMENTID', {
                agreementIDFromParams: agreementID,
                agreementIDFromParamsType: typeof agreementID,
                agreementIDRefCurrent: agreementIDRef.current,
                agreementIDRefType: typeof agreementIDRef.current,
                capturedAgreementID: capturedAgreementID,
                capturedAgreementIDType: typeof capturedAgreementID,
                isFalsy: !capturedAgreementID,
                isNaN: isNaN(capturedAgreementID),
                isLessThanOrEqualZero: capturedAgreementID <= 0,
                // URL analysis
                fullUrl: window.location.href,
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                urlParams: Object.fromEntries(new URLSearchParams(window.location.search))
            });
            console.error('Invalid agreementID at submission', {
                agreementID,
                capturedAgreementID,
                agreementIDRef: agreementIDRef.current
            });
            alert('Session error: Invalid agreement. Please refresh the page and try again.');
            setIsSubmitting(false);
            return;
        }

        if (!formData.contactInfo.trim()) {
            await logDiagnostic('VALIDATION_FAILED_NO_CONTACT_INFO', {
                contactInfoValue: formData.contactInfo,
                contactInfoLength: formData.contactInfo.length,
                allFormDataKeys: Object.keys(formData)
            });
            alert('Please fill in the required contact information field.');
            setIsSubmitting(false);
            return;
        }

        // ===== LOG: All validations passed =====
        await logDiagnostic('VALIDATION_PASSED', {
            capturedAgreementID: capturedAgreementID,
            userID: user.UserID
        });

        const jsonformData = [
            { index: 1, question: 'Product Description', answer: formData.productDescription },
            { index: 2, question: 'Slogan', answer: formData.slogan },
            { index: 3, question: 'Contact Info', answer: formData.contactInfo },
            { index: 4, question: 'Business Name', answer: formData.businessName },
            { index: 5, question: 'Social Media', answer: formData.socialMedia },
            { index: 6, question: 'Certification Numbers', answer: formData.certificationNumbers },
            { index: 7, question: 'Special Offers', answer: formData.specialOffers },
            { index: 8, question: 'Production Notes', answer: formData.productionNotes }
        ];

        const textData = jsonformData.map(item => `${item.question}\n${item.answer}\n\n`).join('');
        const jsonString = JSON.stringify(jsonformData);
        const textBlob = new Blob([textData], { type: 'text/plain' });

        const textFormData = new FormData();
        textFormData.append('file', textBlob, `${advertiserCode}.txt`);
        textFormData.append('advertiserCode', advertiserCode);
        try {
            // Upload file first
            await logDiagnostic('STARTING_TXT_FILE_UPLOAD');
            await axios.post(`${process.env.REACT_APP_BASE_URL}/api/AdPortalUpload/UploadFile`, textFormData);
            await logDiagnostic('TXT_FILE_UPLOAD_SUCCESS');

            console.log('=== AFTER FILE UPLOAD ===');
            console.log('agreementID from useParams (may have changed):', agreementID);
            console.log('capturedAgreementID (safe, unchanged):', capturedAgreementID);
            console.log('Will use capturedAgreementID for tracker update');
            console.log('==========================');

            // Submit tracker update with the CAPTURED value
            await logDiagnostic('STARTING_TRACKER_UPDATE', {
                capturedAgreementID: capturedAgreementID,
                userID: user.UserID,
                dataLength: jsonString.length
            });

            await submitAdTrackerUpdate(jsonString, capturedAgreementID, user.UserID, token);

            await logDiagnostic('TRACKER_UPDATE_SUCCESS');

            setShowSuccessModal(true);
            clearFormData();
        } catch (error) {
            await logDiagnostic('SUBMISSION_ERROR', {
                errorMessage: error.message,
                errorStack: error.stack,
                errorResponseStatus: error.response?.status,
                errorResponseData: error.response?.data
            });

            console.error('Error in form submission:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            alert('There was an issue submitting your ad information. Please try again or contact support.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ===== RACE CONDITION FIX: Accept all values as parameters =====
    const submitAdTrackerUpdate = async (jsonString, validatedAgreementID, userID, token) => {

        // Final validation (defensive programming)
        if (!Number.isInteger(validatedAgreementID) || validatedAgreementID <= 0) {
            console.error('CRITICAL: Invalid AgreementID in submitAdTrackerUpdate', {
                validatedAgreementID,
                type: typeof validatedAgreementID
            });
            throw new Error('Invalid Agreement ID - cannot submit tracker update');
        }

        if (!userID) {
            console.error('CRITICAL: Missing UserID in submitAdTrackerUpdate');
            throw new Error('Missing User ID - cannot submit tracker update');
        }

        const payload = {
            TypeID: 3,
            AgreementID: validatedAgreementID,
            UserID: userID,
            Data: jsonString
        };

        console.log('=== SUBMITTING TRACKER UPDATE ===');
        console.log('Payload:', JSON.stringify(payload, null, 2));
        console.log('================================');

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/CRM/SubmitAdTrackerUpdate`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            console.log('Ad Tracker Update submitted successfully:', response.data);
        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data;

            console.error('Tracker update failed', {
                status,
                data,
                error: error.message,
                payload
            });

            if (status === 401) {
                alert('Your session has expired. Please log in again and re-submit.');
                navigate('/login');
                throw error;
            }

            if ([500, 502, 503, 504].includes(status)) {
                alert('A server error occurred. Please try again in a few minutes.');
                throw error;
            }

            alert('There was an issue submitting your ad information. Please try again.');
            throw error;
        }
    };

    useEffect(() => {
        const warnIfLeaving = (e) => {
            if (isSubmitting) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', warnIfLeaving);
        return () => window.removeEventListener('beforeunload', warnIfLeaving);
    }, [isSubmitting]);

    const clearFormData = () => {
        setFormData({
            productDescription: '',
            slogan: '',
            contactInfo: '',
            businessName: '',
            socialMedia: '',
            certificationNumbers: '',
            specialOffers: '',
            productionNotes: ''
        });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setFileProgress({});
    };

    return (
        <>
            <div className="status-bg">
                <StepTracker />
                <Card className="border-0 status-description">
                    <Card.Body className="pt-0">
                        <Card.Title
                            className="step-title text-center d-flex justify-content-center align-items-center"
                        >
                            Waiting on Assets
                        </Card.Title>
                        <Card.Text>
                            Upload your creative assets and provide details about your ad campaign.
                        </Card.Text>
                    </Card.Body>
                </Card>
            </div>

            <Card className="border-0 m-3">
                <Card.Body>

                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Card>
                                <Card.Header className="account-card-header">
                                    <Form.Label>Upload Creative Assets (logo, images, videos, etc.)</Form.Label>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Control
                                        multiple
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                        type="file"
                                    />
                                    {Object.entries(fileProgress).map(([fileName, progress]) => (
                                        <div key={fileName}>
                                            <p>{fileName}</p>
                                            <ProgressBar
                                                label={`${progress}%`}
                                                now={progress}
                                            />
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>
                        </Form.Group>

                        <Form.Group className="mb-3 text-start">
                            <Card>
                                <Card.Header className="account-card-header">
                                    <Form.Label>Product/Service Description</Form.Label>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Control
                                        as="textarea"
                                        name="productDescription"
                                        onChange={handleInputChange}
                                        rows={3}
                                        value={formData.productDescription}
                                    />
                                </Card.Body>
                            </Card>
                        </Form.Group>

                        <Form.Group className="mb-3 text-start">
                            <Card>
                                <Card.Header className="account-card-header">
                                    <Form.Label>What is your business slogan or catchphrase?</Form.Label>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Control
                                        as="textarea"
                                        name="slogan"
                                        onChange={handleInputChange}
                                        rows={3}
                                        value={formData.slogan}
                                    />
                                </Card.Body>
                            </Card>
                        </Form.Group>

                        <Form.Group className="mb-3 text-start">
                            <Card>
                                <Card.Header className="account-card-header">
                                    <Form.Label>Contact info you would like in your ad? (required)</Form.Label>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Control
                                        as="textarea"
                                        name="contactInfo"
                                        onChange={handleInputChange}
                                        required
                                        rows={3}
                                        value={formData.contactInfo}
                                    />
                                </Card.Body>
                            </Card>
                        </Form.Group>

                        <Form.Group className="mb-3 text-start">
                            <Card>
                                <Card.Header className="account-card-header">
                                    <Form.Label>Business name you would like displayed in ad and pronunciation, if applicable?</Form.Label>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Control
                                        as="textarea"
                                        name="businessName"
                                        onChange={handleInputChange}
                                        rows={3}
                                        value={formData.businessName}
                                    />
                                </Card.Body>
                            </Card>
                        </Form.Group>

                        <Form.Group className="mb-3 text-start">
                            <Card>
                                <Card.Header className="account-card-header">
                                    <Form.Label>Which social media platforms you would like featured in your ad?</Form.Label>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Control
                                        as="textarea"
                                        name="socialMedia"
                                        onChange={handleInputChange}
                                        rows={3}
                                        value={formData.socialMedia}
                                    />
                                </Card.Body>
                            </Card>
                        </Form.Group>

                        <Form.Group className="mb-3 text-start">
                            <Card>
                                <Card.Header className="account-card-header">
                                    <Form.Label>List any special certification numbers, ID's, etc. required in your advertisement.</Form.Label>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Control
                                        as="textarea"
                                        name="certificationNumbers"
                                        onChange={handleInputChange}
                                        rows={3}
                                        value={formData.certificationNumbers}
                                    />
                                </Card.Body>
                            </Card>
                        </Form.Group>

                        <Form.Group className="mb-3 text-start">
                            <Card>
                                <Card.Header className="account-card-header">
                                    <Form.Label>Are there any special offers you would like advertised?</Form.Label>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Control
                                        as="textarea"
                                        name="specialOffers"
                                        onChange={handleInputChange}
                                        rows={3}
                                        value={formData.specialOffers}
                                    />
                                </Card.Body>
                            </Card>
                        </Form.Group>

                        <Form.Group className="mb-3 text-start">
                            <Card>
                                <Card.Header className="account-card-header">
                                    <Form.Label>Production Notes/Special Instructions:</Form.Label>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Control
                                        as="textarea"
                                        name="productionNotes"
                                        onChange={handleInputChange}
                                        rows={3}
                                        value={formData.productionNotes}
                                    />
                                </Card.Body>
                            </Card>
                        </Form.Group>

                        <Button
                            disabled={isSubmitting}
                            type="submit"
                            variant="primary"
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </Button>

                    </Form>

                    <Modal
                        className='submit-success-modal'
                        onHide={() => setShowSuccessModal(false)}
                        show={showSuccessModal}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Success</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>Your information has been successfully uploaded! The tracker will be updated shortly.</Modal.Body>
                        <Modal.Footer>
                            <Button
                                onClick={() => { setShowSuccessModal(false); navigate('/ad-account'); }}
                                variant="secondary"
                            >
                                Close
                            </Button>
                        </Modal.Footer>
                    </Modal>

                </Card.Body>
            </Card >
        </>

    );
};

export default WaitingOnAssets;